/**
 * @module XAxisSetting/XAxisSortBy/useXAxisSortDraft
 * @description X 轴排序功能簇 —— 收敛 sortBy / chartSorts / xAxisValues 的读写与副作用
 *
 * 对外暴露 SortByPanel 所需的 viewModel；协议字段拼装见同目录 `utils.ts`。
 * UI 组件禁止直接 dispatch 或裸 selector 读排序字段。
 *
 * ## 体量说明
 *
 * 本 Hook 约 200 行，但「上传协议 / 排序态映射 / payload 构造」已下沉到 `utils.ts`；
 * 此处主要负责 QB 数据订阅、显隐联动与 SortByPanel 契约组装，属于单一功能簇，
 * 暂不按文件机械拆分；若后续与图例排序抽出公共子 Hook 再评估。
 */

import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { EMPTY_COLUMN_INFO_LIST } from '../../../utils/constants';
import {
  readStructuredArrayRespectingDraftKey,
  useChartSettingsDispatch,
  useChartSettingsSelector,
  usePatchDraft,
  useSettingState,
} from '../../../context';
import { CSActionType } from '../../../types';
import { useQueryColumnMetrics, useQueryCustomColumns, useQueryDataTables, useQuerySelector } from '../../../hooks/deps';
import { useChartsQuickDragList } from '../../../hooks/useChartsQuickDragList';
import { SortByEnum, type TSortByEnum } from '../../common/SortByPanel/SortType';
import type { ICustomMetricSortValueItem } from '../../common/SortByPanel/CustomMetricSort';
import type { IQuickDragItem } from '@dbl/components/QuickDragList/types';
import type { ISortByPanelViewModel } from '../../common/SortByPanel/types';
import {
  SORT_BY_UPLOAD,
  toSortUiMode,
  toXAxisValueStrings,
  seedToXAxisValues,
  buildManualSortPayload,
  buildMetricSortPayload,
  buildDatasetFieldSortPayload,
  buildSortTypeChangePayload,
  resolveMetricSortFromState,
} from './utils';
import { buildDatasetFieldSortResetOnUnresolved } from '../../common/SortByPanel/DatasetFieldSort/datasetFieldSortSync';

export interface IUseXAxisSortDraftOptions {
  /** 由 linkage 裁剪：为 false 时隐藏对应排序方式，并自动回退到默认排序 */
  visibleItems?: Record<string, boolean>;
}

export function useXAxisSortDraft(
  options: IUseXAxisSortDraftOptions = {},
): ISortByPanelViewModel {
  const { visibleItems } = options;
  const { formatMessage, locale } = useIntl();
  const patchDraft = usePatchDraft();

  // ===== 1) 外部数据源：QB 维指 + 图表当前 X 轴类目（手动排序种子） =====
  const columnMetrics = useQueryColumnMetrics();
  const columnDimensions = useQuerySelector(
    s => s.columnDimensions ?? EMPTY_COLUMN_INFO_LIST,
  );
  // 字段排序：必须用 refEqual 订阅，否则左侧改名后下拉不刷新（shallowEqual 误判）
  const dataTables = useQueryDataTables();
  const customColumns = useQueryCustomColumns();
  /** B1：须等 model 确认 schema（区分加载中 vs 已加载可为空），勿用列表非空代理 */
  const dataTablesSchemaReady = useQuerySelector(
    s => s.dataTablesSchemaReady === true,
  );
  /** 来自图表数据的 X 轴类目列表，用于「手动排序」首次落库与空 xAxisValues 时的展示兜底 */
  const quickDragSeed = useChartsQuickDragList();

  // ===== 2) 配置层读取：sortBy（Schema）+ 指标序 / 轴值（Schema） =====
  const { value: sortByRaw } = useSettingState('sortBy');
  /**
   * 指标排序：不用 `useSettingObjectState('chartSorts')`。
   * 上传侧同时存在 chartSorts / customMetricSort，且 hasDraft 需判断「双字段是否已显式清空」，
   * 见 resolveMetricSortFromState。
   */
  const chartSorts = useChartSettingsSelector(s =>
    resolveMetricSortFromState(s.draft, s.base, s.hasDraft),
  );
  const settingsBase = useChartSettingsSelector(s => s.base);
  const settingsDraft = useChartSettingsSelector(s => s.draft);
  const dispatchSettings = useChartSettingsDispatch();
  /**
   * 手动轴序：不用 `useSettingObjectState('xAxisValues')`（其内部为 readStructuredSetting）。
   * 当 draft 上仍有 xAxisValues 键但值非数组时，不得回退 base 里上次 Confirm 的手动顺序；
   * 与图例 series_order 一致，走 readStructuredArrayRespectingDraftKey。写入仍经 patchDraft 多字段 payload。
   */
  const xAxisValuesRaw = useChartSettingsSelector(s =>
    readStructuredArrayRespectingDraftKey(s.draft, s.base, 'xAxisValues'),
  );
  const xAxisValues = useMemo(
    () => toXAxisValueStrings(xAxisValuesRaw),
    [xAxisValuesRaw],
  );

  /** UI 排序态（默认 / 按指标 / 手动 / 字段排序）；底层 upload 仍用 sortBy 协议值 */
  const sortMode = toSortUiMode(sortByRaw);

  /**
   * B1 面板侧兜底：schema 已确认且字段排序引用不可解析时，
   * base+draft 同时回默认（与 model.setTableInfo bridge 一致；不做同名兜底）。
   */
  useEffect(() => {
    if (sortMode !== SortByEnum.DatasetFieldSort) {
      return;
    }
    const both = buildDatasetFieldSortResetOnUnresolved(
      settingsBase,
      settingsDraft,
      dataTables,
      customColumns,
      dataTablesSchemaReady,
    );
    if (!both) {
      return;
    }
    dispatchSettings({
      type: CSActionType.MERGE_QB_PATCH,
      payload: both,
    });
  }, [
    sortMode,
    settingsBase,
    settingsDraft,
    dataTables,
    customColumns,
    dataTablesSchemaReady,
    dispatchSettings,
  ]);

  // ===== 3) linkage 显隐：隐藏项不展示，且需回退 draft 避免 Confirm 带上非法模式 =====
  const hiddenOptions = useMemo(() => {
    const hidden: TSortByEnum[] = [];
    if (visibleItems?.MetricSort === false) {
      hidden.push(SortByEnum.Metric);
    }
    if (visibleItems?.ManualSort === false) {
      hidden.push(SortByEnum.Manual);
    }
    // Step 1：未声明 DatasetFieldSort 时默认展示；显式 false 时隐藏（Step 3 补全工厂）
    if (visibleItems?.DatasetFieldSort === false) {
      hidden.push(SortByEnum.DatasetFieldSort);
    }
    return hidden;
  }, [
    visibleItems?.MetricSort,
    visibleItems?.ManualSort,
    visibleItems?.DatasetFieldSort,
  ]);

  // ===== 4) ref 快照：供 buildSortTypeChangePayload 在稳定回调里读到最新兄弟字段 =====
  const chartSortsRef = useRef(chartSorts);
  chartSortsRef.current = chartSorts;
  const xAxisValuesRef = useRef(xAxisValues);
  xAxisValuesRef.current = xAxisValues;
  const quickDragSeedRef = useRef(quickDragSeed);
  quickDragSeedRef.current = quickDragSeed;
  const columnMetricsRef = useRef(columnMetrics);
  columnMetricsRef.current = columnMetrics;

  const getSortState = useCallback(
    () => ({
      chartSorts: chartSortsRef.current,
      xAxisValues: xAxisValuesRef.current,
      quickDragSeed: quickDragSeedRef.current,
      columnMetrics: (columnMetricsRef.current || []) as unknown[],
    }),
    [],
  );

  const seedStrings = useMemo(
    () => seedToXAxisValues(quickDragSeed),
    [quickDragSeed],
  );

  const lastSyncedSeedKeyRef = useRef('');

  /**
   * 手动排序 + 图表种子变化：把当前 X 轴类目写入 xAxisValues。
   *
   * 业务原因：用户切到「手动」时若 draft 尚无轴序，需用图表实时类目初始化，
   * 否则拖拽区为空且 Confirm 无法带上有效 xAxisValues；种子未变时不重复 patch，避免循环更新。
   */
  useEffect(() => {
    if (sortMode !== SortByEnum.Manual) {
      lastSyncedSeedKeyRef.current = '';
      return;
    }
    if (!seedStrings.length) {
      return;
    }

    const seedKey = seedStrings.join('\0');
    if (lastSyncedSeedKeyRef.current === seedKey) {
      return;
    }
    lastSyncedSeedKeyRef.current = seedKey;

    patchDraft(buildManualSortPayload(seedStrings, quickDragSeed));
  }, [sortMode, seedStrings, quickDragSeed, patchDraft]);

  /**
   * linkage 隐藏当前排序方式时，强制回退「默认排序」并清理关联字段。
   *
   * 例如饼图裁剪掉「按指标排序」、事件折线隐藏「字段排序」：若 draft 仍为对应模式，
   * Confirm 会触发非法重算；需在显隐变化时同步 draft，与面板展示一致。
   */
  useEffect(() => {
    const isHidden =
      (sortMode === SortByEnum.Metric && visibleItems?.MetricSort === false)
      || (sortMode === SortByEnum.Manual && visibleItems?.ManualSort === false)
      || (
        sortMode === SortByEnum.DatasetFieldSort
        && visibleItems?.DatasetFieldSort === false
      );
    if (!isHidden) {
      return;
    }

    patchDraft(buildSortTypeChangePayload(SortByEnum.Default, getSortState()));
  }, [
    sortMode,
    visibleItems?.MetricSort,
    visibleItems?.ManualSort,
    visibleItems?.DatasetFieldSort,
    patchDraft,
    getSortState,
  ]);

  // ===== 5) 用户交互：切换排序方式 / 指标序 / 手动拖拽 =====

  /** 切换默认·指标·手动时，按协议一次性写入 sortBy 及互斥字段（见 buildSortTypeChangePayload） */
  const handleSortTypeChange = useCallback(
    (value: string) => {
      patchDraft(
        buildSortTypeChangePayload(value as TSortByEnum, getSortState()),
      );
    },
    [patchDraft, getSortState],
  );

  /** 指标下拉选项：展示名随 locale 取中英文，value 仍为 column_name 供上传 */
  const customOptions = useMemo(
    () =>
      columnMetrics.map(m => {
        const columnName = String(m.column_name ?? '');
        const isEn = locale === LANGUAGE_EN;
        const displayLabel = isEn
          ? m.column_display_en || m.column_display || columnName
          : m.column_display || columnName;
        return {
          ...m,
          value: columnName,
          table_code: String(m.table_code || 't0'),
          column_display: displayLabel,
        };
      }),
    [columnMetrics, locale],
  );

  /**
   * 手动拖拽列表展示序：已保存的 xAxisValues 优先，否则用图表种子，
   * 保证「未改序」时仍能看到当前轴类目。
   */
  const dragList = useMemo<IQuickDragItem[]>(() => {
    const names = xAxisValues.length > 0 ? xAxisValues : seedStrings;
    return names.map(name => ({ id: name, text: name }));
  }, [xAxisValues, seedStrings]);

  const handleMetricSortChange = useCallback(
    (newValue: ICustomMetricSortValueItem[]) => {
      patchDraft(buildMetricSortPayload(newValue));
    },
    [patchDraft],
  );

  const handleDatasetFieldSortChange = useCallback(
    (newValue: ICustomMetricSortValueItem[]) => {
      patchDraft(buildDatasetFieldSortPayload(newValue));
    },
    [patchDraft],
  );

  /**
   * 手动拖拽结束：sortBy 固定为 x-axis，清空指标排序字段，仅保留 xAxisValues 顺序。
   */
  const handleDragChange = useCallback(
    (items: IQuickDragItem[]) => {
      const ordered = items.map(item => String(item.id ?? item.text ?? ''));
      patchDraft({
        sortBy: SORT_BY_UPLOAD.MANUAL,
        chartSorts: undefined,
        customMetricSort: undefined,
        xAxisValues: ordered,
      });
    },
    [patchDraft],
  );

  /** 手动排序区标题：优先第一个维度显示名，无维度时用「X 轴」文案 */
  const manualDragTitle = useMemo(() => {
    const firstDim = columnDimensions[0];
    const displayName =
      firstDim && 'column_display' in firstDim
        ? String(firstDim.column_display ?? '')
        : '';
    return displayName || formatMessage({ id: 'dbl_chart_x_axis' });
  }, [columnDimensions, formatMessage]);

  // ===== 6) 组装 SortByPanel 契约 =====
  return {
    sortMode,
    hiddenOptions,
    showMetricSort:
      sortMode === SortByEnum.Metric && visibleItems?.MetricSort !== false,
    showManualSort:
      sortMode === SortByEnum.Manual && visibleItems?.ManualSort !== false,
    showDatasetFieldSort:
      sortMode === SortByEnum.DatasetFieldSort
      && visibleItems?.DatasetFieldSort !== false,
    metricSortValue: chartSorts,
    metricSortOptions: customOptions,
    datasetFieldSortValue: chartSorts,
    datasetFieldDataTables: dataTables,
    datasetFieldCustomColumns: customColumns,
    manualDragTitle,
    manualDragList: dragList,
    onSortTypeChange: handleSortTypeChange,
    onMetricSortChange: handleMetricSortChange,
    onDatasetFieldSortChange: handleDatasetFieldSortChange,
    onManualDragChange: handleDragChange,
  };
}
